import React, { useCallback, useEffect, useState, useContext } from 'react';
import type { FC } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';
import type { InlineCommentViewComponentProps } from '@atlaskit/editor-plugins/annotation';

import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { useAnnotationsDispatch } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';
import { fg } from '@confluence/feature-gating';
import {
	handleCommentsPanelNavigationWithViewChange,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import {
	useCommentsPanelActions,
	useCommentsPanelState,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

import { InlineComment } from '../InlineComment';

export type ViewComponentProps = {
	pageId: string;
	editorActions?: EditorActions;
};

export const editorHandleNavigationClick = (nextMarkerRef: string) => {
	const emitter = getEditorAnnotationEventEmitter();
	emitter.emit('setselectedannotation', nextMarkerRef);
};

export const ViewComponent: FC<InlineCommentViewComponentProps & ViewComponentProps> = ({
	annotations: clickedAnnotations,
	getInlineNodeTypes,
	editorActions,
	onClose,
	onDelete,
	isOpeningMediaCommentFromToolbar = false,
	isOffline,
}) => {
	const { setEditorViewProps } = useAnnotationsDispatch();
	const isLivePage = useIsCurrentPageLive();

	const supportedCommentActions: CommentAction[] = !isLivePage
		? ['edit', 'resolve']
		: ['edit', 'resolve', 'delete'];

	const { setCurrentlySelectedThreadKey, setCurrentView } = useCommentsPanelActions();
	const { currentView } = useCommentsPanelState();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
	const { isCommentsPanelShown } = useShowCommentsPanel();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const [isInlineCommentVisible, setIsInlineCommentVisible] = useState<boolean>(false);
	const inlineCommentRef = useCallback((node: any) => {
		if (node === null) {
			setIsInlineCommentVisible(false);
		} else {
			setIsInlineCommentVisible(true);
		}
	}, []);

	const handleResolve = () => {
		setEditorViewProps(null);
	};

	const handleDelete = useCallback(
		(id: string) => {
			setEditorViewProps(null);
			onDelete && onDelete(id);
		},
		[setEditorViewProps, onDelete],
	);

	useEffect(() => {
		const annotationId = clickedAnnotations[0]?.id;
		if (!annotationId) return;

		const shouldScrollCommentsPanelToComment =
			isCommentsPanelShown && clickedAnnotations.length > 0;
		if (shouldScrollCommentsPanelToComment && fg('confluence-frontend-comments-panel')) {
			setCurrentlySelectedThreadKey(annotationId);
			let expectedView = undefined;
			if (
				(currentView === ViewValues.RESOLVED || currentView === ViewValues.DELETED_CONTENT) &&
				fg('confluence_comments_unified_navigation_viewchange')
			) {
				setCurrentView && setCurrentView(ViewValues.OPEN);
				expectedView = ViewValues.OPEN;
			}
			if (fg('confluence_comments_unified_navigation')) {
				handleCommentsPanelNavigationWithViewChange({
					scrollBehavior: ScrollBehavior.SMOOTH,
					context: NavigationContext.PANEL,
					block: 'center',
					annotationId,
					commentType: 'inline',
					annotationClicked: true,
					expectedView,
				});
			} else {
				scrollCommentsPanelToThread({ annotationId, annotationClicked: true });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickedAnnotations[0]?.id]);

	const handleNavigationClick = (nextMarkerRef: string) => {
		if (fg('confluence_frontend_view_inline_comment_arrow_nav')) {
			experienceTracker.updateAttributes({
				name: VIEW_INLINE_COMMENT_EXPERIENCE,
				attributes: {
					annotationId: nextMarkerRef,
				},
			});
		}
		editorHandleNavigationClick(nextMarkerRef);
	};

	const shouldRenderStandaloneInlineComment =
		clickedAnnotations.length > 0 &&
		(!fg('confluence-frontend-comments-panel') || !isCommentsPanelShown);

	return shouldRenderStandaloneInlineComment ? (
		<InlineComment
			isEditor
			annotation={clickedAnnotations[0]}
			getInlineNodeTypes={getInlineNodeTypes}
			onClose={onClose}
			editorActions={editorActions}
			onNavigationClick={(nextMarkerRef: string) => handleNavigationClick(nextMarkerRef)}
			onResolve={handleResolve}
			onDelete={handleDelete}
			supportedTopLevelActions={supportedCommentActions}
			isOpeningMediaCommentFromToolbar={isOpeningMediaCommentFromToolbar}
			inlineCommentRef={inlineCommentRef}
			isInlineCommentVisible={isInlineCommentVisible}
			isOffline={isOffline}
			key={`inline-comment-${clickedAnnotations[0]?.id}`}
		/>
	) : (
		<></>
	);
};
